import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ListContainer from '../components/ListContainer';
import API_BASE_URL from '../config/api';

const CooperativeMachinePerformanceReportInspector = ({ user }) => {
  const navigate = useNavigate();

  // Filter States
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Report Data States
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    noOfMachines: 0,
    chargeableHrs: 0,
    freeHrs: 0,
    totalUtilization: 0,
    avgUtilization: 0,
    totalRevenue: 0,
    avgRevenue: 0
  });

  // Helper to convert DD-Mon-YYYY to YYYY-MM-DD
  const parseDateToISO = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return '';
    const monthMap = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06', Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12' };
    const day = parts[0].padStart(2, '0');
    const month = monthMap[parts[1]] || '01';
    const year = parts[2];
    return `${year}-${month}-${day}`;
  };

  // Fetch Periods for Dropdown & Select Latest by Default
  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/periods`);
        const data = await res.json();
        if (data.success && Array.isArray(data.periods) && data.periods.length > 0) {
          setPeriods(data.periods);
          const latest = data.periods[0]; // Sorted latest first
          setSelectedPeriodId(latest._id);
          setSelectedPeriod(latest);
          if (latest.StartDate && latest.EndDate) {
            setStartDate(parseDateToISO(latest.StartDate));
            setEndDate(parseDateToISO(latest.EndDate));
          }
        }
      } catch (err) {
        console.error('Error fetching periods:', err);
      }
    };
    fetchPeriods();
  }, []);

  // Societies list state for Inspector
  const [societies, setSocieties] = useState([]);
  const [societiesLoaded, setSocietiesLoaded] = useState(false);
  const [selectedSocietyId, setSelectedSocietyId] = useState('ALL');

  // Fetch Societies assigned to Inspector
  useEffect(() => {
    const fetchSocieties = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/societies/inspector?email=${encodeURIComponent(user.email || '')}&phone=${encodeURIComponent(user.phone || '')}&name=${encodeURIComponent(user.name || '')}`);
        const data = await res.json();
        if (data.success && Array.isArray(data.societies)) {
          setSocieties(data.societies);
        }
      } catch (err) {
        console.error('Error fetching inspector societies:', err);
      } finally {
        setSocietiesLoaded(true);
      }
    };
    fetchSocieties();
  }, [user]);

  // Show All Machines vs Top 13 Machines toggle state
  const [showAllMachines, setShowAllMachines] = useState(false);

  // Fetch Report Data from API (Manual trigger via Search)
  const fetchReportData = useCallback(async (socId = selectedSocietyId, perId = selectedPeriodId, sDate = startDate, eDate = endDate, isShowAll = showAllMachines, socList = societies) => {
    if (!sDate || !eDate) return;
    setLoading(true);
    try {
      const societyIdParam = socId === 'ALL'
        ? (socList.length > 0 ? socList.map(s => s._id).join(',') : 'NONE')
        : socId;
      const queryParams = new URLSearchParams({
        societyId: societyIdParam,
        periodId: perId,
        startDate: sDate,
        endDate: eDate,
        showAll: isShowAll ? 'true' : 'false'
      });

      const res = await fetch(`${API_BASE_URL}/api/reports/performance?${queryParams.toString()}`);
      const data = await res.json();

      if (data.success) {
        setRows(data.rows || []);
        setTotals(data.totals || {});
      }
    } catch (err) {
      console.error('Error fetching performance report:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSocietyId, selectedPeriodId, startDate, endDate, showAllMachines, societies]);

  // Initial load when period, dates, and societies are initialized
  const [initialLoaded, setInitialLoaded] = useState(false);
  useEffect(() => {
    if (!initialLoaded && startDate && endDate && societiesLoaded) {
      fetchReportData(selectedSocietyId, selectedPeriodId, startDate, endDate, showAllMachines, societies);
      setInitialLoaded(true);
    }
  }, [startDate, endDate, selectedSocietyId, selectedPeriodId, fetchReportData, initialLoaded, showAllMachines, societiesLoaded, societies]);

  // Search Handler
  const handleSearch = () => {
    fetchReportData(selectedSocietyId, selectedPeriodId, startDate, endDate, showAllMachines);
  };

  // Toggle Show More / Show Less Machines
  const handleToggleShowMachines = () => {
    const nextVal = !showAllMachines;
    setShowAllMachines(nextVal);
    fetchReportData(selectedSocietyId, selectedPeriodId, startDate, endDate, nextVal);
  };

  // Clear Handler (resets filters to default latest period)
  const handleClear = () => {
    setShowAllMachines(false);
    setSelectedSocietyId('ALL');
    if (periods.length > 0) {
      const latest = periods[0];
      setSelectedPeriodId(latest._id);
      setSelectedPeriod(latest);
      if (latest.StartDate && latest.EndDate) {
        const sISO = parseDateToISO(latest.StartDate);
        const eISO = parseDateToISO(latest.EndDate);
        setStartDate(sISO);
        setEndDate(eISO);
        fetchReportData('ALL', latest._id, sISO, eISO, false);
      }
    }
  };

  // Handle Period Dropdown Selection (Auto updates Start and End Date)
  const handlePeriodChange = (e) => {
    const periodId = e.target.value;
    setSelectedPeriodId(periodId);
    const matched = periods.find(p => p._id === periodId);
    if (matched) {
      setSelectedPeriod(matched);
      if (matched.StartDate && matched.EndDate) {
        setStartDate(parseDateToISO(matched.StartDate));
        setEndDate(parseDateToISO(matched.EndDate));
      }
    }
  };

  // Restrict Start & End date pickers strictly between period boundaries
  const periodMinISO = selectedPeriod ? parseDateToISO(selectedPeriod.StartDate) : '';
  const periodMaxISO = selectedPeriod ? parseDateToISO(selectedPeriod.EndDate) : '';

  const handleStartDateChange = (e) => {
    const val = e.target.value;
    if (periodMinISO && val < periodMinISO) return;
    if (periodMaxISO && val > periodMaxISO) return;
    setStartDate(val);
  };

  const handleEndDateChange = (e) => {
    const val = e.target.value;
    if (periodMinISO && val < periodMinISO) return;
    if (periodMaxISO && val > periodMaxISO) return;
    setEndDate(val);
  };

  // Export CSV Handler
  const handleExportCSV = () => {
    if (rows.length === 0) return;
    
    const headers = ["Machines", "No. of Machines", "Authorized Machine Count", "Chargeable Hrs", "Free Hrs", "Total Utilization", "Avg. Utilization", "Total Revenue", "Avg. Revenue"];
    const csvRows = rows.map(r => [
      `"${r.name}"`,
      r.noOfMachines,
      r.authNoOfMachines || 0,
      r.chargeableHrs,
      r.freeHrs,
      r.totalUtilization,
      r.avgUtilization,
      r.totalRevenue,
      r.avgRevenue
    ].join(','));

    csvRows.push([
      "TOTALS",
      totals.noOfMachines,
      totals.authNoOfMachines || 0,
      totals.chargeableHrs,
      totals.freeHrs,
      totals.totalUtilization,
      totals.avgUtilization,
      totals.totalRevenue,
      totals.avgRevenue
    ].join(','));

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...csvRows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Inspector_Performance_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render Filter Bar
  const filterBar = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center' }}>
        {/* Society Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Society:</label>
          <select
            value={selectedSocietyId}
            onChange={(e) => setSelectedSocietyId(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              background: '#ffffff',
              outline: 'none',
              fontWeight: '500'
            }}
          >
            <option value="ALL">All Assigned Societies ({societies.length})</option>
            {societies.map(s => (
              <option key={s._id} value={s._id}>{s.SOCIETY || s.SocietyName}</option>
            ))}
          </select>
        </div>

        {/* Period Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Period:</label>
          <select
            value={selectedPeriodId}
            onChange={handlePeriodChange}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              background: '#ffffff',
              outline: 'none',
              fontWeight: '500'
            }}
          >
            {periods.map(p => {
              const label = `${p.StartDate} to ${p.EndDate} (${p.seasonName || p.PERIOD})`;
              return <option key={p._id} value={p._id}>{label}</option>;
            })}
          </select>
        </div>

        {/* Date Range Inputs restricted to period boundaries */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Date Range:</label>
          <input
            type="date"
            value={startDate}
            min={periodMinISO}
            max={periodMaxISO}
            onChange={handleStartDateChange}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
          />
          <span style={{ color: '#64748b', fontSize: '12px' }}>to</span>
          <input
            type="date"
            value={endDate}
            min={periodMinISO}
            max={periodMaxISO}
            onChange={handleEndDateChange}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
          />
        </div>

        {/* Search & Clear Action Buttons */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleSearch}
            style={{
              background: '#f59e0b',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 18px',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleClear}
            style={{
              background: '#f1f5f9',
              color: '#475569',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              padding: '8px 16px',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Right Side Buttons above Grid */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button
          type="button"
          onClick={handleToggleShowMachines}
          style={{
            background: '#4318ff',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          {showAllMachines ? 'Show Less (Top 13)' : 'Show More (All Machines)'}
        </button>

        {/* Export CSV Button */}
        <button
          type="button"
          onClick={handleExportCSV}
          disabled={rows.length === 0}
          style={{
            background: rows.length > 0 ? '#10b981' : '#cbd5e1',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: rows.length > 0 ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          📊 Export CSV
        </button>
      </div>
    </div>
  );

  return (
    <ListContainer
      title="Cooperative Machine Performance Report"
      onClose={() => navigate('/home')}
      filterBar={filterBar}
    >
      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#ffffff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0', color: '#334155' }}>
              <th rowSpan="2" style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0', fontWeight: '700', verticalAlign: 'middle', width: '200px' }}>
                Machines
              </th>
              <th rowSpan="2" style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0', fontWeight: '700', textAlign: 'center', verticalAlign: 'middle', width: '110px' }}>
                No. of Machines
              </th>
              <th rowSpan="2" style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0', fontWeight: '700', textAlign: 'center', verticalAlign: 'middle', width: '140px' }}>
                Authorized Machine Count
              </th>
              <th colSpan="4" style={{ padding: '10px 16px', borderRight: '1px solid #e2e8f0', fontWeight: '700', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                Utilization (Hrs Rented)
              </th>
              <th colSpan="2" style={{ padding: '10px 16px', fontWeight: '700', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                Revenue Generation (Rental Charges)
              </th>
            </tr>
            <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '12px' }}>
              <th style={{ padding: '10px 14px', borderRight: '1px solid #e2e8f0', textAlign: 'right', fontWeight: '600' }}>Chargeable Hrs</th>
              <th style={{ padding: '10px 14px', borderRight: '1px solid #e2e8f0', textAlign: 'right', fontWeight: '600' }}>Free Hrs</th>
              <th style={{ padding: '10px 14px', borderRight: '1px solid #e2e8f0', textAlign: 'right', fontWeight: '600' }}>Total Utilization</th>
              <th style={{ padding: '10px 14px', borderRight: '1px solid #e2e8f0', textAlign: 'right', fontWeight: '600' }}>Avg. Utilization</th>
              <th style={{ padding: '10px 14px', borderRight: '1px solid #e2e8f0', textAlign: 'right', fontWeight: '600' }}>Total Revenue</th>
              <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '600' }}>Avg. Revenue</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  <div className="loading-spinner-container">
                    <div className="spinner"></div>
                    <div>Loading Performance Report...</div>
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  No performance data available for selected period and dates.
                </td>
              </tr>
            ) : (
              <>
                {rows.map((row, idx) => (
                  <tr
                    key={row.code}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      background: idx % 2 === 0 ? '#ffffff' : '#f8fafc',
                      transition: 'background 0.15s ease'
                    }}
                  >
                    <td style={{ padding: '12px 16px', fontWeight: '600', color: '#1e293b', borderRight: '1px solid #f1f5f9' }}>
                      {row.name}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', color: '#334155', borderRight: '1px solid #f1f5f9' }}>
                      {row.noOfMachines}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', color: '#334155', borderRight: '1px solid #f1f5f9', fontWeight: '600' }}>
                      {row.authNoOfMachines || 0}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#334155', borderRight: '1px solid #f1f5f9' }}>
                      {row.chargeableHrs}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#334155', borderRight: '1px solid #f1f5f9' }}>
                      {row.freeHrs}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#0f172a', borderRight: '1px solid #f1f5f9' }}>
                      {row.totalUtilization}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#334155', borderRight: '1px solid #f1f5f9' }}>
                      {row.avgUtilization}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#15803d', borderRight: '1px solid #f1f5f9' }}>
                      ₹{row.totalRevenue.toLocaleString('en-IN')}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#334155' }}>
                      ₹{row.avgRevenue.toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
                {/* Summary Totals Row */}
                <tr style={{ background: '#f1f5f9', borderTop: '2px solid #cbd5e1', fontWeight: '700', color: '#0f172a' }}>
                  <td style={{ padding: '14px 16px', borderRight: '1px solid #cbd5e1' }}>TOTALS</td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid #cbd5e1' }}>{totals.noOfMachines}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid #cbd5e1' }}>{totals.authNoOfMachines || 0}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1' }}>{totals.chargeableHrs}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1' }}>{totals.freeHrs}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1' }}>{totals.totalUtilization}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1' }}>{totals.avgUtilization}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', borderRight: '1px solid #cbd5e1', color: '#15803d' }}>
                    ₹{(totals.totalRevenue || 0).toLocaleString('en-IN')}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                    ₹{(totals.avgRevenue || 0).toLocaleString('en-IN')}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </ListContainer>
  );
};

export default CooperativeMachinePerformanceReportInspector;
